import React, { useEffect, useRef, useState } from 'react';
import { createChart, IChartApi, ISeriesApi, CandlestickData, HistogramData, UTCTimestamp } from 'lightweight-charts';
import { ChartData, TradeMarker, DrawingData, DrawingTool } from '../types';
import DrawingOverlay from './DrawingOverlay';

interface CandleInfo {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

interface TradingChartProps {
  chartData: ChartData;
  trades: TradeMarker[];
  drawings: DrawingData | null;
  selectedTool: string;
  onCandleHover?: (candleInfo: CandleInfo | null) => void;
}

const TradingChart: React.FC<TradingChartProps> = ({
  chartData,
  trades,
  drawings,
  selectedTool,
  onCandleHover
}) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candlestickSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const volumeSeriesRef = useRef<ISeriesApi<'Histogram'> | null>(null);
  const [chartDimensions, setChartDimensions] = useState({ width: 0, height: 0 });
  const [hasFittedContent, setHasFittedContent] = useState(false);
  const [hasSetInitialView, setHasSetInitialView] = useState(false);

  // Initialize chart
  useEffect(() => {
    if (!chartContainerRef.current) return;

    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: chartContainerRef.current.clientHeight,
      autoSize: true, // Enable automatic resizing
      layout: {
        background: { color: '#131722' },
        textColor: '#d1d4dc',
        attributionLogo: false, // Remove TradingView logo to save space
      },
      grid: {
        vertLines: {
          color: '#363a45',
          style: 0,
        },
        horzLines: {
          color: '#363a45',
          style: 0,
        },
      },
      crosshair: {
        mode: 0, // Previous crosshair setting as requested
      },
      rightPriceScale: {
        borderColor: '#485158',
        textColor: '#d1d4dc',
      },
      timeScale: {
        borderColor: '#485158',
        timeVisible: true,
        secondsVisible: false,
        visible: true,
        ticksVisible: true,
        rightOffset: 50, // Add right margin to prevent label cutoff
        borderVisible: true, // Show time scale border
        minimumHeight: 40, // Ensure minimum height for time axis visibility
        // Force UTC time display to match broker time
        // Note: TradingView Lightweight Charts automatically handles timezone conversion
        // but we can ensure consistent display
      },
    });

    // Create candlestick series
    const candlestickSeries = chart.addCandlestickSeries({
      upColor: '#26a69a',
      downColor: '#ef5350',
      borderDownColor: '#ef5350',
      borderUpColor: '#26a69a',
      wickDownColor: '#ef5350',
      wickUpColor: '#26a69a',
    });

    // Create volume series
    const volumeSeries = chart.addHistogramSeries({
      color: '#26a69a',
      priceFormat: {
        type: 'volume',
      },
      priceScaleId: 'volume',
    });

    chart.priceScale('volume').applyOptions({
      scaleMargins: {
        top: 0.8,
        bottom: 0,
      },
    });

    chartRef.current = chart;
    candlestickSeriesRef.current = candlestickSeries;
    volumeSeriesRef.current = volumeSeries;

    // Subscribe to crosshair move events for OHLC display
    chart.subscribeCrosshairMove((param) => {
      if (onCandleHover && param.time) {
        const candleData = param.seriesData.get(candlestickSeries);
        if (candleData) {
          const candle = candleData as CandlestickData;
          const volumeData = param.seriesData.get(volumeSeries) as HistogramData;
          
          onCandleHover({
            time: param.time as number,
            open: candle.open,
            high: candle.high,
            low: candle.low,
            close: candle.close,
            volume: volumeData?.value || 0
          });
        }
      } else if (onCandleHover) {
        onCandleHover(null);
      }
    });

    // Store chart dimensions for overlay
    const updateDimensions = () => {
      if (chartContainerRef.current) {
        setChartDimensions({
          width: chartContainerRef.current.clientWidth,
          height: chartContainerRef.current.clientHeight
        });
      }
    };

    updateDimensions();
    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(chartContainerRef.current);

    // Handle resize
    const handleResize = () => {
      if (chartContainerRef.current && chart) {
        chart.applyOptions({
          width: chartContainerRef.current.clientWidth,
          height: chartContainerRef.current.clientHeight,
        });
        updateDimensions();
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      resizeObserver.disconnect();
      chart.remove();
    };
  }, []);

  // Calculate extended time range including all drawing objects
  const getExtendedTimeRange = (bars: any[], drawings: any[]) => {
    if (!bars.length) return { minTime: 0, maxTime: 0 };
    
    // Get candlestick time range
    const barTimes = bars.map(bar => bar.time);
    let minTime = Math.min(...barTimes);
    let maxTime = Math.max(...barTimes);
    
    // Extend range to include drawing object timestamps
    if (drawings && drawings.length > 0) {
      drawings.forEach(drawing => {
        if (drawing.time1) {
          minTime = Math.min(minTime, drawing.time1);
          maxTime = Math.max(maxTime, drawing.time1);
        }
        if (drawing.time2) {
          minTime = Math.min(minTime, drawing.time2);
          maxTime = Math.max(maxTime, drawing.time2);
        }
      });
      
      console.log(`📅 Extended time range: bars ${new Date(Math.min(...barTimes) * 1000).toISOString()} to ${new Date(Math.max(...barTimes) * 1000).toISOString()}`);
      console.log(`📅 Drawing range: ${new Date(minTime * 1000).toISOString()} to ${new Date(maxTime * 1000).toISOString()}`);
    }
    
    return { minTime, maxTime };
  };

  // Update chart data
  useEffect(() => {
    if (!candlestickSeriesRef.current || !volumeSeriesRef.current || !chartData.bars.length) return;

    // Get extended time range to include all drawings
    const { minTime, maxTime } = getExtendedTimeRange(chartData.bars, drawings?.drawings || []);
    
    let candlestickData: CandlestickData[] = chartData.bars
      .sort((a, b) => a.time - b.time)
      .map(bar => ({
        time: bar.time as UTCTimestamp,
        open: bar.open,
        high: bar.high,
        low: bar.low,
        close: bar.close,
      }));

    let volumeData: HistogramData[] = chartData.bars
      .sort((a, b) => a.time - b.time)
      .map(bar => ({
        time: bar.time as UTCTimestamp,
        value: bar.volume,
        color: bar.close >= bar.open ? '#26a69a40' : '#ef535040',
      }));

    // Extend time axis if drawing objects require it
    const lastBarTime = Math.max(...chartData.bars.map(bar => bar.time));
    if (maxTime > lastBarTime) {
      console.log(`📈 Extending time axis from ${new Date(lastBarTime * 1000).toISOString()} to ${new Date(maxTime * 1000).toISOString()}`);
      
      // Collect all unique timestamps from drawing objects that are beyond last bar
      const futureTimestamps = new Set<number>();
      
      if (drawings?.drawings) {
        drawings.drawings.forEach(drawing => {
          if (drawing.time1 && drawing.time1 > lastBarTime) {
            futureTimestamps.add(drawing.time1);
          }
          if (drawing.time2 && drawing.time2 > lastBarTime) {
            futureTimestamps.add(drawing.time2);
          }
        });
      }
      
      // Sort timestamps and add placeholder candlesticks at exact times
      const sortedTimestamps = Array.from(futureTimestamps).sort();
      const lastBar = chartData.bars[chartData.bars.length - 1];
      
      console.log(`📅 Adding placeholder candlesticks for ${sortedTimestamps.length} exact drawing timestamps`);
      
      sortedTimestamps.forEach(timestamp => {
        console.log(`   Adding placeholder at ${new Date(timestamp * 1000).toISOString()}`);
        candlestickData.push({
          time: timestamp as UTCTimestamp,
          open: lastBar.close,
          high: lastBar.close,
          low: lastBar.close,
          close: lastBar.close,
        });
        
        volumeData.push({
          time: timestamp as UTCTimestamp,
          value: 0, // No volume for extended points
          color: '#26a69a10',
        });
      });
    }

    candlestickSeriesRef.current.setData(candlestickData);
    volumeSeriesRef.current.setData(volumeData);
    
    // Time axis extension is already handled by adding placeholder candlesticks
    // No need for fitContent() which overrides the user's view
    if (maxTime > lastBarTime && !hasFittedContent) {
      setHasFittedContent(true);
      console.log(`📊 Time axis extended with placeholder candlesticks - no fitContent needed`);
    }
  }, [chartData, drawings?.drawings]);

  // Separate effect for initial centering - only runs once when chart dimensions are ready
  useEffect(() => {
    const setCenteredView = () => {
      if (chartRef.current && chartDimensions.width > 0 && chartData?.bars?.length > 0) {
        // Calculate visible range to center chart avoiding left panel
        const panelWidth = 320;
        const chartWidth = chartDimensions.width;
        const availableWidth = chartWidth - panelWidth;
        
        console.log(`🔧 Initial centering - panelWidth: ${panelWidth}, chartWidth: ${chartWidth}, availableWidth: ${availableWidth}`);
        
        // Calculate bars to show based on available width (roughly 10px per bar for better spacing)
        const barsToShow = Math.floor(availableWidth / 10);
        const totalBars = chartData.bars.length;
        
        if (totalBars > barsToShow && barsToShow > 0) {
          // Show recent bars centered in available space
          const fromIndex = Math.max(0, totalBars - barsToShow);
          const fromTime = chartData.bars[fromIndex].time;
          const toTime = chartData.bars[totalBars - 1].time;
          
          chartRef.current.timeScale().setVisibleLogicalRange({
            from: fromTime as number,
            to: toTime as number
          });
          
          console.log(`📊 Chart initially centered: showing ${barsToShow} bars (from index ${fromIndex} to ${totalBars-1})`);
        } else {
          console.log(`⚠️ Skipping initial centering: totalBars=${totalBars}, barsToShow=${barsToShow}`);
        }
        
        setHasSetInitialView(true);
      }
    };

    // Only run initial centering once when we have proper dimensions and data
    if (!hasSetInitialView && chartDimensions.width > 0 && chartData?.bars?.length > 0) {
      console.log(`🚀 TRIGGERING INITIAL CENTERING - hasSetInitialView: ${hasSetInitialView}, width: ${chartDimensions.width}, bars: ${chartData.bars.length}`);
      setTimeout(setCenteredView, 300); // Give chart time to fully initialize
    } else {
      console.log(`❌ CENTERING CONDITIONS NOT MET - hasSetInitialView: ${hasSetInitialView}, width: ${chartDimensions.width}, bars: ${chartData?.bars?.length || 0}`);
    }
  }, [chartDimensions.width, chartData?.bars?.length, hasSetInitialView]);

  // Add trade markers
  useEffect(() => {
    if (!candlestickSeriesRef.current || !trades.length) return;

    // Clear existing markers
    candlestickSeriesRef.current.setMarkers([]);

    const markers = trades.map(trade => ({
      time: trade.time as UTCTimestamp,
      position: trade.type === 'BUY' || trade.type === 'TP' ? 'belowBar' : 'aboveBar',
      color: trade.type === 'BUY' ? '#26a69a' : 
             trade.type === 'SELL' ? '#ef5350' : 
             trade.type === 'TP' ? '#4caf50' : '#f44336',
      shape: trade.type === 'BUY' ? 'arrowUp' : 
             trade.type === 'SELL' ? 'arrowDown' : 'circle',
      text: `${trade.type} ${trade.lot_size}`,
      size: 1,
    }));

    candlestickSeriesRef.current.setMarkers(markers as any);
  }, [trades]);

  const getChartCoordinates = (time: number, price: number) => {
    if (!chartRef.current || !candlestickSeriesRef.current) return null;

    try {
      const timeScale = chartRef.current.timeScale();
      const x = timeScale.timeToCoordinate(time as UTCTimestamp);
      const y = candlestickSeriesRef.current.priceToCoordinate(price);

      if (x === null || y === null) {
        console.log(`❌ Coordinate conversion failed: time=${time} (${new Date(time * 1000).toISOString()}) -> X=${x}, price=${price} -> Y=${y}`);
        return null;
      }

      console.log(`✅ Coordinate conversion: time=${time} (${new Date(time * 1000).toISOString()}) -> X=${x}, price=${price} -> Y=${y}`);
      return { x, y };
    } catch (error) {
      console.warn('Error converting chart coordinates:', error);
      return null;
    }
  };

  const getChartPrice = (x: number, y: number) => {
    if (!chartRef.current || !candlestickSeriesRef.current) return null;

    try {
      const timeScale = chartRef.current.timeScale();
      const time = timeScale.coordinateToTime(x);
      
      // Use series to get price from coordinate
      const price = candlestickSeriesRef.current.coordinateToPrice(y);

      if (time === null || price === null) return null;

      return { time: time as number, price };
    } catch (error) {
      console.warn('Error converting chart price:', error);
      return null;
    }
  };

  const getPriceScaleWidth = () => {
    if (!chartRef.current) return 70; // fallback
    
    try {
      const rightPriceScale = chartRef.current.priceScale('right');
      const priceScaleWidth = rightPriceScale.width();
      console.log(`📏 Precise price scale width: ${priceScaleWidth}px`);
      return priceScaleWidth;
    } catch (error) {
      console.warn('Error getting price scale width:', error);
      return 70; // fallback
    }
  };

  return (
    <div className="trading-chart" style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div ref={chartContainerRef} style={{ 
        width: '100%', 
        height: '100%'
      }} />
      
      <DrawingOverlay
        width={chartDimensions.width}
        height={chartDimensions.height}
        drawings={drawings}
        selectedTool={selectedTool as DrawingTool}
        getChartCoordinates={getChartCoordinates}
        getChartPrice={getChartPrice}
        getPriceScaleWidth={getPriceScaleWidth}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          pointerEvents: selectedTool === 'cursor' ? 'none' : 'auto' // Allow drawing interactions
        }}
      />
    </div>
  );
};

export default TradingChart;